/**
 * BetaWelcomeBack Component
 * Automatic welcome experience for returning Beta users
 * Detects Beta users on login and offers to import their journey data
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Rocket, Award, Users, Heart, Brain, DollarSign,
  CheckCircle, ArrowRight, Sparkles, Upload, 
  ChevronRight, Trophy, Star, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { DragonflyUserService } from '../services/DragonflyUserService';
import { BetaDataImportService, BetaExportData } from '../services/BetaDataImportService';
import { supabase } from '../lib/supabase';

interface BetaJourneyProgress {
  currentStep: number;
  completedModules: string[];
  totalStories: number;
  connections: number;
  hasPostFlight: boolean;
  journeyCompleted: boolean;
}

export const BetaWelcomeBack: React.FC = () => {
  const { user } = useSupabaseAuth();
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [betaProgress, setBetaProgress] = useState<BetaJourneyProgress | null>(null);
  const [importStatus, setImportStatus] = useState<'idle' | 'importing' | 'success' | 'error'>('idle');
  const [isDragonflyUser, setIsDragonflyUser] = useState(false);
  const [sourcePlatform, setSourcePlatform] = useState<string>('');

  useEffect(() => {
    const checkBetaUser = async () => {
      if (!user?.email) {
        setIsLoading(false);
        return;
      }

      try {
        // Check if user is from Dragonfly platform
        const dragonflyInfo = await DragonflyUserService.checkDragonflyUser(user.email);
        
        if (dragonflyInfo.isDragonflyUser) {
          setIsDragonflyUser(true);
          setSourcePlatform(dragonflyInfo.sourcePlatform || 'Beta');
          
          // Check if we've already shown welcome back (store in localStorage)
          const hasSeenWelcome = localStorage.getItem(`welcome_back_${user.id}`);
          
          if (!hasSeenWelcome) {
            // Check if user has Beta data in localStorage or hover_users table
            const hasBetaData = await BetaDataImportService.checkBetaUserData(user.id);
            
            // Check hover_users table for beta_user flag
            const { data: hoverUser } = await supabase
              .from('hover_users')
              .select('beta_user, journey_data')
              .eq('email', user.email)
              .single();
            
            if (dragonflyInfo.hasCompletedJourney || hasBetaData || hoverUser?.beta_user) {
              // Create mock progress for now (would be fetched from Beta API)
              setBetaProgress({
                currentStep: dragonflyInfo.journeyProgress || 6,
                completedModules: ['LIFE', 'MONEY', 'JOY'],
                totalStories: 12,
                connections: 8,
                hasPostFlight: true,
                journeyCompleted: dragonflyInfo.hasCompletedJourney || false
              });
              
              setShow(true);
            }
          }
        }
      } catch (error) {
        console.error('Error checking Beta user status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkBetaUser();
  }, [user]);

  const handleImportData = async () => {
    setImportStatus('importing');
    
    try {
      // Check for cached Beta data
      const cachedData = localStorage.getItem(`beta_data_${user?.id}`);
      
      if (cachedData) {
        // Parse and import cached data
        const betaData = JSON.parse(cachedData) as BetaExportData;
        await importBetaDataToHover(betaData);
      } else {
        // Prompt user to upload their Beta export
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        
        input.onchange = async (e) => {
          const file = (e.target as HTMLInputElement).files?.[0];
          if (file) {
            const betaData = await BetaDataImportService.importBetaExport(file);
            await importBetaDataToHover(betaData);
          }
        };
        
        input.click();
      }
    } catch (error) {
      console.error('Error importing Beta data:', error);
      setImportStatus('error');
    }
  };

  const importBetaDataToHover = async (betaData: BetaExportData) => {
    try {
      // Map Beta data to Hover's RedHelicopterSchema
      const lifeProgress = betaData.lifeStories?.stories.length ? 
        Math.min((betaData.lifeStories.stories.length / 10) * 100, 100) : 0;
      
      const journeyProgress = betaData.journey?.progress || {};
      
      // Update hover_users with journey data
      const { error } = await supabase
        .from('hover_users')
        .update({
          journey_data: {
            modules: {
              LIFE: {
                narrativeDepth: lifeProgress,
                valuesClarity: journeyProgress.valuesClarity || 50,
                foundationStability: journeyProgress.foundationStability || 50,
                completedAt: betaData.journey?.completedSteps.includes(3) ? new Date().toISOString() : null
              },
              MONEY: {
                systemsUnderstanding: journeyProgress.moneyProgress || 50,
                resourceAwareness: journeyProgress.resourceAwareness || 50,
                decisionAlignment: journeyProgress.decisionAlignment || 50,
                completedAt: betaData.journey?.completedSteps.includes(4) ? new Date().toISOString() : null
              },
              JOY: {
                connectionDepth: journeyProgress.joyProgress || 50,
                communityContribution: journeyProgress.communityContribution || 50,
                vulnerabilityCapacity: journeyProgress.vulnerabilityCapacity || 50,
                completedAt: betaData.journey?.completedSteps.includes(5) ? new Date().toISOString() : null
              }
            },
            breakthroughs: [],
            milestones: [],
            importedFrom: 'beta',
            importedAt: new Date().toISOString(),
            originalData: betaData
          }
        })
        .eq('email', user?.email);
      
      if (error) throw error;
      
      setImportStatus('success');
      
      // Mark as seen
      localStorage.setItem(`welcome_back_${user?.id}`, 'true');
      
      // Close after delay
      setTimeout(() => {
        setShow(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error saving imported data:', error);
      setImportStatus('error');
    }
  };

  const handleSkip = () => {
    localStorage.setItem(`welcome_back_${user?.id}`, 'true');
    setShow(false);
  };

  if (isLoading || !show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="max-w-4xl w-full"
        >
          <Card className="bg-gradient-to-br from-purple-950/90 to-indigo-950/90 border-purple-500/30">
            <CardHeader className="text-center pb-4">
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="flex justify-center mb-4"
              >
                <div className="relative">
                  <Rocket className="w-16 h-16 text-purple-400" />
                  <motion.div
                    className="absolute -top-2 -right-2"
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ repeat: Infinity, duration: 3 }}
                  >
                    <Sparkles className="w-6 h-6 text-yellow-400" />
                  </motion.div>
                </div>
              </motion.div>
              
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Welcome Back, Journey Graduate! 
              </CardTitle>
              
              <p className="text-gray-300 mt-2">
                We detected you've been on an amazing journey at {sourcePlatform}
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Journey Summary */}
              {betaProgress && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-black/30 rounded-lg p-4"
                >
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-400" />
                    Your Journey Achievements
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Completed Modules */}
                    <div className="space-y-2">
                      <p className="text-sm text-gray-400">Completed Modules</p>
                      <div className="flex gap-2">
                        {betaProgress.completedModules.includes('LIFE') && (
                          <Badge className="bg-purple-600/20 text-purple-300 border-purple-500/30">
                            <Brain className="w-3 h-3 mr-1" />
                            LIFE
                          </Badge>
                        )}
                        {betaProgress.completedModules.includes('MONEY') && (
                          <Badge className="bg-green-600/20 text-green-300 border-green-500/30">
                            <DollarSign className="w-3 h-3 mr-1" />
                            MONEY
                          </Badge>
                        )}
                        {betaProgress.completedModules.includes('JOY') && (
                          <Badge className="bg-pink-600/20 text-pink-300 border-pink-500/30">
                            <Heart className="w-3 h-3 mr-1" />
                            JOY
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Stories Created */}
                    <div className="space-y-2">
                      <p className="text-sm text-gray-400">Agency Stories</p>
                      <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-400" />
                        <span className="text-2xl font-bold text-white">{betaProgress.totalStories}</span>
                      </div>
                    </div>
                    
                    {/* Connections Made */}
                    <div className="space-y-2">
                      <p className="text-sm text-gray-400">Community Connections</p>
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-400" />
                        <span className="text-2xl font-bold text-white">{betaProgress.connections}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-gray-400 mb-1">
                      <span>Journey Progress</span>
                      <span>{Math.round((betaProgress.currentStep / 6) * 100)}%</span>
                    </div>
                    <Progress value={(betaProgress.currentStep / 6) * 100} className="h-2" />
                  </div>
                  
                  {/* PostFlight Badge */}
                  {betaProgress.hasPostFlight && (
                    <div className="mt-4 flex items-center gap-2 text-green-400">
                      <Shield className="w-5 h-5" />
                      <span className="text-sm font-semibold">PostFlight License Earned!</span>
                    </div>
                  )}
                </motion.div>
              )}

              {/* What's New in Hover */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-lg p-4"
              >
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  Continue Your Journey with AI-Powered Features
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Personalized Flight Path</p>
                      <p className="text-xs text-gray-400">AI-generated missions based on your progress</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Enhanced Journey Videos</p>
                      <p className="text-xs text-gray-400">Create beautiful videos of your transformation</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">AI Co-Navigator</p>
                      <p className="text-xs text-gray-400">Intelligent guidance for your next phase</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Community Intelligence</p>
                      <p className="text-xs text-gray-400">Connect with similar journey travelers</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-3"
              >
                {importStatus === 'idle' && (
                  <>
                    <Button
                      onClick={handleImportData}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                      size="lg"
                    >
                      <Upload className="w-5 h-5 mr-2" />
                      Import My Journey Data
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                    
                    <Button
                      onClick={handleSkip}
                      variant="outline"
                      className="flex-1 border-gray-600 hover:bg-gray-800"
                      size="lg"
                    >
                      Start Fresh in Hover
                    </Button>
                  </>
                )}
                
                {importStatus === 'importing' && (
                  <div className="flex-1 flex items-center justify-center py-3">
                    <div className="flex items-center gap-3 text-purple-400">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                      >
                        <Upload className="w-6 h-6" />
                      </motion.div>
                      <span>Importing your journey data...</span>
                    </div>
                  </div>
                )}
                
                {importStatus === 'success' && (
                  <motion.div
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    className="flex-1 flex items-center justify-center py-3"
                  >
                    <div className="flex items-center gap-3 text-green-400">
                      <CheckCircle className="w-6 h-6" />
                      <span className="font-semibold">Journey data imported successfully!</span>
                    </div>
                  </motion.div>
                )}
                
                {importStatus === 'error' && (
                  <div className="flex-1">
                    <div className="text-red-400 text-center mb-3">
                      Unable to import automatically. Please upload your Beta export file.
                    </div>
                    <Button
                      onClick={handleImportData}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
                      size="lg"
                    >
                      <Upload className="w-5 h-5 mr-2" />
                      Select Export File
                    </Button>
                  </div>
                )}
              </motion.div>

              {/* Privacy Note */}
              <p className="text-xs text-gray-500 text-center">
                Your journey data is securely stored and never shared. You can manage your imported data anytime in settings.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};